import { createContext } from '@utils/createContext';
import React, { useMemo, useRef } from 'react';
import { AuthContextType, AuthProviderProps } from './auth.types';
import { AuthApi } from './api/api';
import { useAccessToken } from './useAccessToken';
import { useDocumentVisibility } from '@hooks/useDocumentVisibility';
import { useIdentity } from './useIdentity';
import { useStorageChange } from '@utils/storage/useStorageChange';
import { useIframeMessages } from './useIframeMessages';

const [Provider, useAuthContext] = createContext<AuthContextType>({ name: 'AuthProvider' });

export const AuthProvider = ({ children, loginManager }: AuthProviderProps) => {
  const apiRef = useRef(new AuthApi(loginManager));
  const {
    // state
    hasInitializedOnce,
    user,
    // handlers
    checkAndProcessAccessToken,
    processAccessToken,
    provideLogout,
    switchToEvent,
  } = useIdentity({ apiRef, loginManager });

  // Subscribe to access token refresh
  useAccessToken(loginManager, processAccessToken);

  // Check access token when user switches tabs
  useDocumentVisibility({
    onVisible: () => {
      checkAndProcessAccessToken();
    },
  });

  // When user modifies local storage in the context of another document (iframe, tab)
  useStorageChange(checkAndProcessAccessToken);

  useIframeMessages({ switchToEvent });

  const contextValue = useMemo<AuthContextType>(() => {
    return {
      hasInitializedOnce,
      isLoggedIn: !!user.profile,
      currentUser: user,
      provideLogout,
      switchToEvent,
    };
  }, [hasInitializedOnce, user, provideLogout, switchToEvent]);
  return <Provider value={contextValue}>{children}</Provider>;
};

export { useAuthContext };
